<script setup lang="ts">
/**
 * Client detail card hosted in the info panel (right side of `InfoViewLayout`).
 *
 * Receives the selected client's `personId` prop and runs five queries:
 * client metadata, service types, service categories, and the two streams
 * merged by `useAlignedClientStreams` (balance events + visit history). Three
 * auto-drain `watch` handlers page-through the reference catalogs to completion
 * so the transaction table can resolve type names without a secondary fetch per
 * row. `MonthlyBarChart` visualises the client's monthly session history. The
 * Edit button opens `AddClientForm` in edit mode via `useAddClientFormStore`.
 */
import { computed, markRaw, onUnmounted, ref, toRef, watch, watchEffect } from 'vue'
import type { Component as DtComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import DateTimeCell from '@/components/table/cell/DateTimeCell.vue'
import TransactionLabelCell from './cells/TransactionLabelCell.vue'
import SignedAmountCell from './cells/SignedAmountCell.vue'
import RunningBalanceCell from './cells/RunningBalanceCell.vue'
import EventRowCell from './cells/EventRowCell.vue'
import VisitNumberCell from './cells/VisitNumberCell.vue'
import MonthlyBarChart from '@/components/stats/MonthlyBarChart.vue'
import {
  fetchBusinessServiceTypes,
  type BusinessServiceTypesGroup,
} from '@/api/businessServiceTypes'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { deleteClient, fetchClient } from '@/api/clients'
import { useAlignedClientStreams, type MergedRow } from '@/composables/useAlignedClientStreams'
import { useClientSubscriptions } from '@/composables/useClientSubscriptions'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import {
  clientCardPollingInterval,
  serviceCategoriesPollingInterval,
  serviceTypesPollingInterval,
} from '@/config/api'
import { defaultPrecision } from '@/config/money'
import { majorToMinor } from '@/utils/majorToMinor'
import { formatMoney, moneyColumnLabel } from '@/utils/formatMoney'
import { formatDuration } from '@/utils/formatDuration'
import { formatName } from '@/utils/formatName'
import { formatStartDateFromIso } from '@/utils/formatStartDate'
import { formatDateTime } from '@/utils/formatDateTime'
import { defaultLocaleForDate } from '@/config/date'
import { useAddClientFormStore } from '@/stores/add_client_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'

/**
 * Client info card hosted by `InfoViewLayout`. Receives the selected client's
 * id and renders the person header, stats, contact, monthly-visits bar chart
 * and a scrollable merged ledger of lessons / payments / monthly-balance
 * markers / disease ranges.
 *
 * Progressive loading: every section above the table renders as soon as its
 * own data resolves (e.g. the header appears the moment `/persons/{id}`
 * comes back, even while the four list streams are still in flight).
 * The visits area shows `loading-tufte.gif` until the streams have completed
 * their initial fetch *and* the alignment loop has caught laggy streams up;
 * only then does the `DynamicTable` mount with a stable, aligned dataset.
 */
const props = defineProps<{
  /** Client id of the currently selected client; drives every per-client query. */
  clientId: string
  /** When true, hides the built-in Edit button (e.g. when the host provides its own navbar Edit button). */
  hideEditBtn?: boolean
}>()

const { t } = useI18n()

const clientId = toRef(props, 'clientId')
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.ClientsEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.ClientsDelete))

const clientQuery = useQuery({
  queryKey: ['client', clientId],
  queryFn: () => fetchClient(clientId.value),
  enabled: computed(() => !!clientId.value),
  staleTime: clientCardPollingInterval,
  refetchInterval: clientCardPollingInterval,
  refetchIntervalInBackground: false,
})

const clientSubscriptions = useClientSubscriptions(clientId)

const streams = useAlignedClientStreams(
  clientId,
  {
    payments: computed(() => hasRole(ResourceAccessRole.PaymentsView)),
    services: computed(() => hasRole(ResourceAccessRole.BusinessServicesView)),
    balances: computed(() => hasRole(ResourceAccessRole.BalancesView)),
    diseases: computed(() => hasRole(ResourceAccessRole.DiseasesView)),
  },
  clientSubscriptions.subscriptions,
)

/**
 * Subscriptions still running, for the header list: the backend keeps them
 * `is_active` until they expire, and an exhausted one stays listed (amber)
 * until then so staff see why visits are being charged again.
 */
const activeSubscriptions = computed(() =>
  clientSubscriptions.subscriptions.value.filter((s) => s.isActive),
)

function formatShortDate(iso: string | null): string {
  if (!iso) return ''
  return formatDateTime({ date: iso, dateStyle: 'short' }).formattedDate
}

function subscriptionCounter(sub: { used: number | null; total: number | null }): string {
  if (sub.used === null) return ''
  return sub.total === null ? String(sub.used) : `${sub.used}/${sub.total}`
}

function subscriptionUsedLabel(sub: { used: number | null; total: number | null }): string {
  if (sub.used === null) return ''
  return sub.total === null
    ? t('clientCard.subscriptionUsedUnlimited', { used: sub.used })
    : t('clientCard.subscriptionUsed', { used: sub.used, total: sub.total })
}

/** Month the header debt belongs to: `latest_balance` closes the previous month. */
const debtMonthLabel = computed<string>(() => {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return new Intl.DateTimeFormat(defaultLocaleForDate.value, { month: 'long' }).format(d)
})

/*
 * Service-type catalogue lookup for lesson labels, powered by TanStack
 * Query (instead of the former Pinia cache store). Both catalogue streams
 * are cursor-paginated, so we auto-drain pages to mirror the previous
 * "collect all" behavior and keep id lookups complete.
 */
const serviceTypeGroupsQuery = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceTypesPollingInterval,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
  enabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView)),
})

const serviceNamesQuery = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceCategoriesPollingInterval,
  refetchInterval: serviceCategoriesPollingInterval,
  refetchIntervalInBackground: false,
  enabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesView)),
})

watch(
  () => ({
    hasNext: serviceTypeGroupsQuery.hasNextPage.value === true,
    fetching: serviceTypeGroupsQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void serviceTypeGroupsQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => ({
    hasNext: serviceNamesQuery.hasNextPage.value === true,
    fetching: serviceNamesQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void serviceNamesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

interface ServiceTypeDisplay {
  // Resolved display data for one business service type.

  name: string
  // Service type label (description or category name fallback).

  duration: string | null
  // Formatted duration (e.g. `1h 30m`); null when not available.
}

const serviceTypeDisplayById = computed<Map<string, ServiceTypeDisplay>>(() => {
  const categoryNameById = new Map<string, string>()
  for (const page of serviceNamesQuery.data.value?.pages ?? []) {
    for (const item of page.items ?? []) {
      categoryNameById.set(item.id, item.name)
    }
  }

  const out = new Map<string, ServiceTypeDisplay>()
  const groups: BusinessServiceTypesGroup[] = (
    serviceTypeGroupsQuery.data.value?.pages ?? []
  ).flatMap((page) => page.items ?? [])
  for (const group of groups) {
    for (const t of group.business_service_types ?? []) {
      const desc = typeof t.description === 'string' ? t.description.trim() : ''
      const categoryName = categoryNameById.get(group.business_service_name_id)
      const name = desc.length > 0 ? desc : categoryName
      if (!name) continue
      const duration = formatDuration(t.duration) ?? null
      out.set(t.id, { name, duration })
    }
  }
  return out
})

const person = computed(() => clientQuery.data.value ?? null)

const displayName = computed(() => {
  const raw = person.value?.name?.trim()
  return raw ? formatName(raw) : ''
})

const genderLabel = computed(() => {
  const raw = person.value?.gender?.trim().toLowerCase()
  if (!raw) return ''
  return raw
})

/**
 * Most-recent monthly balance amount (in minor units), used by the header.
 * Prefers `person.current_balance` because the backend computes it directly;
 * falls back to the newest balance snapshot from the balances stream when
 * the person endpoint returns `null` (often the case with seed data).
 */
const headerBalanceMinor = computed<number | undefined>(() => {
  const cb = person.value?.current_balance
  const converted = majorToMinor(cb?.amount)
  if (converted !== undefined) return converted
  const rows = streams.rows.value
  /* `rows` is sorted DESC, so the first balance-event encountered is the
   * newest. Fall through to undefined when the streams have not produced any
   * balance markers yet — the cell will show its placeholder. */
  for (const r of rows) {
    if (r.kind === 'balance-event' && typeof r.balanceAmountMinor === 'number') {
      return r.balanceAmountMinor
    }
  }
  return undefined
})

const headerBalanceFormatted = computed<string | null>(() => {
  if (headerBalanceMinor.value === undefined) return null
  const out = formatMoney({ amount: headerBalanceMinor.value }).formatted
  if (headerBalanceMinor.value > 0) return `+${out}`
  return out
})

const headerBalanceClass = computed<'pos' | 'neg' | 'muted'>(() => {
  const m = headerBalanceMinor.value
  if (m === undefined) return 'muted'
  if (m < 0) return 'neg'
  if (m > 0) return 'pos'
  return 'muted'
})

/**
 * Debt (minor units), from `latest_balance` — balance as of the 1st of the
 * current month plus payments made so far this month. Mirrors `DebtCell`:
 * a non-negative `latest_balance` means last month's debt is closed, so it
 * always displays as 0, not the raw (possibly positive) value.
 */
const debtMinor = computed<number | undefined>(() => majorToMinor(person.value?.latest_balance))

const isDebt = computed(() => debtMinor.value !== undefined && debtMinor.value < 0)

const debtFormatted = computed<string | null>(() => {
  if (debtMinor.value === undefined) return null
  return formatMoney({ amount: isDebt.value ? debtMinor.value : 0 }).formatted
})

/**
 * Formatted "Since" pieces for the header: absolute date + relative phrase.
 * Returns empty strings when `start_datetime` is missing or unparsable so
 * the template can keep its muted `–` placeholder for both halves at once.
 */
const sinceParts = computed(() => formatStartDateFromIso(person.value?.start_datetime))

const startDateLabel = computed<string>(() => sinceParts.value.formattedDate)
const startDurationLabel = computed<string>(() => sinceParts.value.durationSince)

/** Lessons attended in the loaded window, drawn from the merged ledger. */
const lessonRows = computed(() => streams.rows.value.filter((r) => r.kind === 'lesson'))

const visitsTotal = computed<number | null>(() => {
  const fromApi = person.value?.business_service_count
  if (typeof fromApi === 'number' && Number.isFinite(fromApi)) return fromApi
  if (streams.isInitialLoading.value) return null
  return lessonRows.value.length
})

/**
 * Lessons in the last 30 days, computed from the loaded ledger so the
 * card stays accurate without an extra API call. Counts lessons on or after
 * the calendar day 30 days ago (midnight local time), not a raw 30*24h
 * timestamp subtraction — the latter dropped a lesson dated exactly 30 days
 * back the moment today's clock time ticked past that lesson's time-of-day,
 * which read as visits silently vanishing from the count within the same day.
 */
const visitsLastMonth = computed<number | null>(() => {
  if (streams.isInitialLoading.value) return null
  const cutoffDate = new Date()
  cutoffDate.setHours(0, 0, 0, 0)
  cutoffDate.setDate(cutoffDate.getDate() - 30)
  const cutoff = cutoffDate.getTime()
  let count = 0
  for (const r of lessonRows.value) {
    const t = new Date(r.date).getTime()
    if (Number.isFinite(t) && t >= cutoff) count++
  }
  return count
})

/**
 * Sum of every loaded payment in major currency units. Partial when more
 * pages exist (the visits area still shows the loading gif while the
 * alignment loop runs, so the value the user sees here only changes once
 * all streams are settled).
 */
const totalPaidMajor = computed<number | null>(() => {
  if (streams.isInitialLoading.value) return null
  let totalMinor = 0
  for (const r of streams.rows.value) {
    if (r.kind === 'payment' && typeof r.deltaMinor === 'number') {
      totalMinor += r.deltaMinor
    }
  }
  return totalMinor / 10 ** defaultPrecision
})

const totalPaidFormatted = computed<string | null>(() => {
  if (totalPaidMajor.value === null) return null
  return formatMoney({
    amount: majorToMinor(totalPaidMajor.value) ?? 0,
  }).formatted
})

/**
 * Number of monthly buckets the sparkline should span: from the client's
 * `start_datetime` through the current month, inclusive. Falls back to
 * `12` while the person endpoint is still loading or when the start date
 * is missing/unparsable so the chart renders an empty 12-bar grid rather
 * than collapsing to zero width.
 *
 * Capped at `120` (10 years) as a safety net for clients with bogus dates
 * far in the past. The chart's SVG scales the bar width to fit so any
 * count from 1 up renders correctly; the cap exists purely to bound the
 * inner loop and label density when something upstream returns garbage.
 */
const sparkMonthCount = computed<number>(() => {
  const iso = person.value?.start_datetime
  if (typeof iso !== 'string' || iso.trim() === '') return 12
  const start = new Date(iso)
  if (Number.isNaN(start.getTime())) return 12
  const now = new Date()
  const dy = now.getFullYear() - start.getFullYear()
  const dm = now.getMonth() - start.getMonth()
  const diff = dy * 12 + dm + 1
  if (!Number.isFinite(diff) || diff <= 0) return 1
  return Math.min(diff, 120)
})

/**
 * Per-month bucket counts of lessons relative to today. Index `0` is the
 * oldest month in the displayed window (the client's start month), and
 * index `sparkMonthCount.value - 1` is the current month. Drives the
 * sparkline; an all-zero array renders the empty grid.
 *
 * The previous fixed 24-month window was replaced because the chart sits
 * above the visits ledger and the user expects scrolling further back to
 * line up with the bars they see — a fixed cutoff hid older months once
 * a client has been on the books for over two years.
 */
const sparkBuckets = computed<number[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const nowYear = now.getFullYear()
  const nowMonth = now.getMonth()
  const bins = new Array<number>(months).fill(0)
  for (const r of lessonRows.value) {
    const d = new Date(r.date)
    if (Number.isNaN(d.getTime())) continue
    const dy = nowYear - d.getFullYear()
    const dm = nowMonth - d.getMonth()
    const diff = dy * 12 + dm
    if (diff >= 0 && diff < months) bins[months - 1 - diff]! += 1
  }
  return bins
})

/**
 * Per-row visit numbers for `lesson` rows in the merged ledger.
 *
 * The list endpoints only know about the rows we have already loaded, so
 * the "absolute" visit number — the position of a lesson in the client's
 * full history — must be derived from `business_service_count` returned
 * by `/persons/{id}`. When that count is available, the newest loaded
 * lesson is `count`, the next is `count - 1`, and so on; this stays
 * correct even before the alignment loop has fetched older pages, because
 * we only need the count of *unloaded* older lessons to be implicit in
 * the starting offset.
 *
 * When `business_service_count` is `null` (the API does not track the
 * total) we fall back to numbering inside the loaded window only. The
 * numbers are then accurate iff the services stream has no more pages;
 * otherwise older lessons may shift as more pages stream in. This is the
 * least-bad option — better than leaving the column blank — and matches
 * the loading-state behaviour of the rest of the card.
 */
const lessonNumberByRowId = computed<Map<string, number>>(() => {
  const out = new Map<string, number>()
  const lessons = lessonRows.value
  if (lessons.length === 0) return out
  const fromApi = person.value?.business_service_count
  const total =
    typeof fromApi === 'number' && Number.isFinite(fromApi) && fromApi > 0
      ? fromApi
      : lessons.length
  /* `lessons` is sorted DESC by date (newest first) just like the merged
   * ledger, so the first element is the most recent visit and gets the
   * highest number. */
  let n = total
  for (const r of lessons) {
    out.set(r.rowId, n)
    n -= 1
  }
  return out
})

/**
 * Month labels aligned with `sparkBuckets`. Uses month-only formatting
 * (`Mar`) for short windows where the year is unambiguous, and falls
 * back to month + 2-digit year (`Mar '24`) once the window is over a
 * year so the same month name in different years doesn't read as a
 * single bar repeated twice.
 */
const sparkLabels = computed<string[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const showYear = months > 12
  const formatter = new Intl.DateTimeFormat(
    defaultLocaleForDate.value,
    showYear ? { month: 'short', year: '2-digit' } : { month: 'short' },
  )
  const labels: string[] = []
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1)
    labels.push(formatter.format(d))
  }
  return labels
})

/**
 * Per-bucket labels rendered inside the chart's hover tooltip. We always
 * use month-only formatting here regardless of the window length: the
 * x-axis already conveys the year (e.g. `Apr 25`), so repeating it in
 * the popup just makes it wider for no extra information. A user
 * hovering a specific bar can read the year off the nearest axis tick.
 */
const sparkTooltipLabels = computed<string[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const formatter = new Intl.DateTimeFormat(defaultLocaleForDate.value, {
    month: 'short',
  })
  const labels: string[] = []
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1)
    labels.push(formatter.format(d))
  }
  return labels
})

/**
 * Stride between rendered x-axis labels. Targets ~4 labels regardless of
 * total month count so the axis stays readable from a 1-month window
 * (one label) up through 10-year windows (every ~30th month). Without
 * this scaling, the previous fixed stride of `6` produced 20 labels for
 * a 10-year history, which overflowed the 240-px chart.
 */
const sparkLabelStride = computed<number>(() => {
  const months = sparkMonthCount.value
  return Math.max(1, Math.ceil(months / 4))
})

/**
 * Section title for the sparkline. The title used to be a fixed
 * "Visits — last 24 months", but the chart now spans the client's full
 * history; surfacing the actual month count up front lets the user
 * confirm at a glance which window the bars represent (and matches the
 * user's request: "show how many months in a graph"). Singular-vs-plural
 * is handled explicitly so a brand-new client doesn't read
 * "last 1 months". `…` is shown while `person` is still loading instead
 * of guessing a window the chart has not yet been told to render.
 */
const sparkSectionLabel = computed<string>(() => {
  if (person.value === null) return t('clientCard.visitsSparkLoading')
  const months = sparkMonthCount.value
  if (months <= 1) return t('clientCard.visitsSparkLabelSingular')
  return t('clientCard.visitsSparkLabelPlural', { months })
})

/**
 * Column config for the merged visits table. Built once because the cell
 * components are stable; per-row data still flows through `props(row)`.
 *
 * Widths are tuned to the panel's effective width (`--width-left-column`
 * minus the `client-card`'s padding and the panel's own gutters) so the
 * five columns fit without horizontal scrolling. The Date column stacks
 * the calendar date over the time of day (mirrors the rest of the app's
 * `DateTimeCell`); the Type column has no `width` so it absorbs the
 * remaining horizontal space and lets long service-type names wrap onto
 * additional rows inside the cell.
 */
const visitsColumns = computed<Column<MergedRow>[]>(() => [
  {
    key: 'visit-number',
    label: '#',
    component: markRaw(VisitNumberCell),
    props: (row) => ({
      /* Only `lesson` rows get a number; payments leave the cell blank.
       * `balance-event` / `disease-event` rows never reach this cell because
       * `pickRowOverride` collapses them into a single full-width cell. */
      visitNumber: row.kind === 'lesson' ? lessonNumberByRowId.value.get(row.rowId) : undefined,
    }),
    /* `0.9rem` is just wide enough for a 2-digit count ("99") at the
     * table's `--text-tufte-xs` body density. Combined with the tight
     * scoped padding overrides on the first column below, the cell hugs
     * the number itself rather than reserving a multi-character gutter.
     * Counts of 100+ flow into the next column on the rare wide row;
     * that's an acceptable trade-off for the tight default look the
     * user asked for. */
    width: '0.9rem',
    align: 'right',
  },
  {
    key: 'date',
    label: t('common.date'),
    component: markRaw(DateTimeCell),
    /* `DateTimeCell` stacks the calendar date (`27 Nov 2025`) over the
     * time of day (`23:00`) on two lines. The deep CSS rule on the
     * second column (see the `<style scoped>` block below) sets
     * `white-space: nowrap` so each of the two lines stays on a single
     * line — the previous bug where "27 Nov 2025" wrapped to three
     * lines was a missing nowrap, not the cell choice. */
    props: (row) => ({
      date: row.date,
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
    width: '5.75rem',
  },
  {
    key: 'type',
    label: t('clientCard.colType'),
    component: markRaw(TransactionLabelCell),
    props: (row) => {
      /* Lessons resolve their label through the service-types store so the
       * client card matches the wording used in the Service Types tab
       * (e.g. `бачата база`). Only the catalogue *name* is shown; the
       * duration is intentionally omitted because the Date column already
       * carries a time-of-day line that gives the user the temporal
       * context, and the row was getting visually crowded with the
       * additional muted line. The composable's `Service #<id>` fallback
       * covers two transient cases: (1) the store has not finished its
       * first load yet, (2) the id is unknown to the catalogue (e.g. a
       * deleted type). Payments always use the literal `Payment` label
       * already set by the composable. */
      if (row.kind === 'payment') {
        return {
          label: row.label ?? '',
          kind: 'payment' as const,
          subtitle: null,
        }
      }
      const display =
        row.serviceTypeId == null ? undefined : serviceTypeDisplayById.value.get(row.serviceTypeId)
      const label = display?.name ?? row.label ?? ''
      return {
        label,
        kind: 'lesson' as const,
        subtitle: null,
      }
    },
    /* No fixed `width`: the Type column flexes into whatever room is left
     * after the four numeric / date columns. Long service-type names wrap
     * onto additional lines inside the cell instead of pushing the table
     * sideways (see `TransactionLabelCell`'s wrapping rules). */
  },
  {
    key: 'delta',
    /* `moneyColumnLabel` appends the narrow currency symbol (e.g. "Δ ($)")
     * so the cells themselves can keep showing numbers only — same pattern as
     * the `Balance` / `Amount` / `Price` columns elsewhere in the app. */
    label: moneyColumnLabel('Δ'),
    component: markRaw(SignedAmountCell),
    props: (row) => ({
      amountMinor: row.deltaMinor,
      kind: row.kind === 'payment' ? 'payment' : 'lesson',
      basePriceMinor: row.basePriceMinor,
      covered: row.kind === 'lesson' && !!row.subscriptionId,
    }),
    // Widened from 3.5rem: discounted lessons now stack a struck-through
    // "was" price above the charged amount (see `SignedAmountCell`), which
    // needs a touch more room than a single money value did.
    width: '4rem',
    align: 'right',
  },
  {
    key: 'balance',
    label: moneyColumnLabel(t('clientCard.colBalanceAbbr')),
    component: markRaw(RunningBalanceCell),
    props: (row) => ({
      balanceMinor: row.balanceMinor,
      flat: row.kind === 'lesson' && !!row.subscriptionId,
    }),
    width: '3.75rem',
    align: 'right',
  },
])

/**
 * Selects `EventRowCell` for monthly-balance and disease rows so the table
 * renders one full-width `<td colspan>` instead of the four normal cells.
 * Returns `null` for transaction rows so the regular cell pipeline applies.
 */
function pickRowOverride(row: unknown): DtComponent | null {
  const r = row as MergedRow
  if (
    r.kind === 'balance-event' ||
    r.kind === 'disease-event' ||
    r.kind === 'subscription-event' ||
    r.kind === 'payment-subscription' ||
    r.kind === 'lesson-subscription'
  ) {
    return markRaw(EventRowCell)
  }
  return null
}

/**
 * Stable row identity used by `DynamicTable.rowKey`. Pulls the precomputed
 * `MergedRow.rowId` so re-renders keep the same `<tr>` mapped to the same
 * source record (matters for selection state and Vue keying).
 */
function rowKeyFn(row: unknown): string {
  return (row as MergedRow).rowId
}

const showLoadingGif = ref(false)
let loadingTimer: ReturnType<typeof setTimeout> | null = null
watchEffect(() => {
  if (streams.isInitialLoading.value || streams.isAligning.value) {
    if (loadingTimer === null) {
      loadingTimer = setTimeout(() => {
        showLoadingGif.value = true
        loadingTimer = null
      }, 300)
    }
  } else {
    if (loadingTimer !== null) {
      clearTimeout(loadingTimer)
      loadingTimer = null
    }
    showLoadingGif.value = false
  }
})
onUnmounted(() => {
  if (loadingTimer !== null) clearTimeout(loadingTimer)
})

const addClientFormStore = useAddClientFormStore()

function openEdit(): void {
  const p = person.value
  if (!p) return
  const rawGender = p.gender?.trim().toUpperCase()
  const gender: 'MALE' | 'FEMALE' = rawGender === 'MALE' || rawGender === 'M' ? 'MALE' : 'FEMALE'
  // 'client', not 'addClientForm': clearInfoViewSelections clears every
  // OTHER store, so exempting the form would wipe this card's own
  // clientCardStore selection. Since InfoViewLayout picks what to render
  // off that same selection, losing it here means the panel goes empty
  // (not back to this card) the moment the edit form closes.
  clearInfoViewSelections('client')
  addClientFormStore.openForEdit(p.id, {
    name: p.name ?? '',
    gender,
    phone: p.phone ?? null,
    email: p.email ?? null,
    roleIds: (p.roles ?? []).map((r) => r.id),
    startDatetime: p.start_datetime ?? null,
    birthday: null,
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteClient(clientId.value),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['clients'] })
    showDeleteConfirm.value = false
    // Clears every selection store, including this card's own — on mobile
    // that also drops `clientCardStore.hasSelection`, which is what gates
    // `ClientCardMobileScreen`'s `v-if` in `ClientsTab`, so the overlay
    // closes itself without a dedicated `deleted` emit/listener pair.
    clearInfoViewSelections()
  },
})

const deleteDescription = computed<string>(() => {
  const p = person.value
  const who = displayName.value || p?.name || t('clientCard.defaultClientName')
  return t('clientCard.deleteDescription', { who })
})

function handleDelete(): void {
  if (!person.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <section
    class="client-card font-tufte text-tufte-text"
    :aria-label="t('clientCard.cardAriaLabel')"
  >
    <!-- Name row stays pinned; everything under it, the ledger included,
         scrolls as one area. -->
    <!-- Header: name + gender + current balance. Renders as soon as the
         person query resolves; the balance line shows its muted placeholder
         when no snapshot is available yet. -->
    <div class="client-card__header">
      <div class="client-card__name">
        <template v-if="displayName">{{ displayName }}</template>
        <span v-else class="text-tufte-muted">{{ t('common.loadingShort') }}</span>
      </div>
      <div class="client-card__meta-row">
        <span class="client-card__gender">{{ genderLabel }}</span>
        <div
          v-if="person && !hideEditBtn && (canEdit || canDelete)"
          class="client-card__header-actions"
        >
          <button v-if="canEdit" type="button" class="client-card__edit-btn" @click="openEdit">
            <svg
              width="10"
              height="10"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="vertical-align: -0.1em; margin-right: 0.2rem"
            >
              <path d="M9.5 2.5l2 2L5 11l-2.5.5L3 9z" />
              <path d="M8.5 3.5l2 2" /></svg
            >{{ t('common.edit') }}
          </button>
          <button
            v-if="canDelete"
            type="button"
            class="client-card__delete-btn-top"
            @click="handleDelete"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="vertical-align: -0.1em; margin-right: 0.2rem"
            >
              <path d="M2.5 4h9" />
              <path d="M5.5 4V2.7a.8.8 0 0 1 .8-.8h1.4a.8.8 0 0 1 .8.8V4" />
              <path d="M3.5 4l.6 7.5a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9L10.5 4" />
              <path d="M6 6.3v4M8 6.3v4" /></svg
            >{{ t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <div class="client-card__scroll">
      <!-- Money: debt for last month and the current balance side by side. -->
      <div class="client-card__money-card">
        <div class="client-card__money-cell">
          <span class="client-card__money-label">{{
            t('clientCard.debtForMonth', { month: debtMonthLabel })
          }}</span>
          <span
            class="client-card__money-value"
            :class="isDebt ? 'client-card__money-value--neg' : 'client-card__money-value--pos'"
          >
            <template v-if="debtFormatted">{{ debtFormatted }}</template>
            <span v-else class="text-tufte-muted">…</span>
          </span>
        </div>
        <div class="client-card__money-cell">
          <span class="client-card__money-label">{{ t('clientCard.currentBalance') }}</span>
          <span
            class="client-card__money-value"
            :class="`client-card__money-value--${headerBalanceClass}`"
          >
            <template v-if="headerBalanceFormatted">{{ headerBalanceFormatted }}</template>
            <span v-else class="text-tufte-muted">–</span>
          </span>
        </div>
      </div>

      <!-- Running subscriptions: name and counter on one line, dates below. -->
      <div
        v-if="clientSubscriptions.canView.value && !clientSubscriptions.isLoading.value"
        class="client-card__subs"
        :title="t('clientCard.activeSubscriptions')"
      >
        <div v-if="activeSubscriptions.length > 0" class="client-card__subs-list">
          <div
            v-for="sub in activeSubscriptions"
            :key="sub.id"
            class="client-card__subs-item"
            :title="subscriptionUsedLabel(sub)"
          >
            <div class="client-card__subs-name">
              <span class="client-card__subs-title">{{
                sub.name || t('clientCard.subscriptionTag')
              }}</span>
              <span
                v-if="subscriptionCounter(sub)"
                class="client-card__subs-used"
                :class="{ 'client-card__subs-used--spent': sub.isExhausted }"
                >{{ subscriptionCounter(sub) }}</span
              >
            </div>
            <div class="client-card__subs-dates">
              {{
                [formatShortDate(sub.validFrom), formatShortDate(sub.validUntil)]
                  .filter(Boolean)
                  .join(' — ')
              }}
            </div>
          </div>
        </div>
        <div v-else class="client-card__subs-empty">
          {{ t('clientCard.activeSubscriptionsNone') }}
        </div>
      </div>

      <!-- Stats: derived from the person endpoint and the loaded ledger; each
         value shows its own placeholder while still pending. "Since" takes a
         line, the three counters share the next one. -->
      <div class="client-card__stats">
        <div class="client-card__stat-line">
          <span class="client-card__stat-label">{{ t('common.since') }}</span>
          <span class="client-card__stat-value client-card__stat-value--success">
            <template v-if="startDateLabel">
              {{ startDateLabel
              }}<template v-if="startDurationLabel"> · {{ startDurationLabel }}</template>
            </template>
            <span v-else class="text-tufte-muted">–</span>
          </span>
        </div>
        <div class="client-card__stat-grid">
          <div class="client-card__stat-cell">
            <span class="client-card__stat-label">{{ t('clientCard.visitsTotal') }}</span>
            <span class="client-card__stat-value">
              <template v-if="visitsTotal !== null">{{ visitsTotal }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="client-card__stat-cell">
            <span class="client-card__stat-label">{{ t('clientCard.last30Days') }}</span>
            <span class="client-card__stat-value client-card__stat-value--stale">
              <template v-if="visitsLastMonth !== null">{{ visitsLastMonth }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="client-card__stat-cell">
            <span class="client-card__stat-label">{{ t('clientCard.totalPaid') }}</span>
            <span class="client-card__stat-value client-card__stat-value--success">
              <template v-if="totalPaidFormatted">{{ totalPaidFormatted }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Contact: phone and email on one wrapping line; collapses when empty. -->
      <div v-if="person && (person.phone || person.email)" class="client-card__contact">
        <span v-if="person.phone" class="client-card__contact-item">{{ person.phone }}</span>
        <span
          v-if="person.email"
          class="client-card__contact-item client-card__contact-item--muted"
          >{{ person.email }}</span
        >
      </div>

      <!-- Sparkline: lessons-per-month bar chart spanning the client's full
         history (start month → current month). Renders an empty grid
         until the services stream produces its first page; the bar count
         and label stride adapt to the window length so a brand-new client
         gets ~1 bar and a long-running client gets the full timeline. -->
      <div class="client-card__spark">
        <div class="client-card__spark-label">{{ sparkSectionLabel }}</div>
        <MonthlyBarChart
          :buckets="sparkBuckets"
          :labels="sparkLabels"
          :tooltip-labels="sparkTooltipLabels"
          :label-stride="sparkLabelStride"
          :width="240"
          :height="40"
        />
      </div>

      <!-- Visits & payments table. Header strip is sticky at the top of the
         scrollable body so it stays visible while the user scrolls the
         merged ledger. The table itself only mounts once the streams have
         a stable aligned frame; before that the loading gif replaces it. -->
      <div class="client-card__visits">
        <div class="client-card__visits-header">{{ t('clientCard.visitsAndPayments') }}</div>
        <div v-if="showLoadingGif" class="client-card__visits-loading">
          <img
            src="/loading-tufte.gif"
            alt=""
            class="client-card__loading-gif"
            width="240"
            height="24"
          />
          <p class="text-tufte-muted">{{ t('clientCard.aligningStreams') }}</p>
        </div>
        <DynamicTable
          v-else
          class="client-card__visits-table"
          :columns="visitsColumns as Column<unknown>[]"
          :data="streams.rows.value"
          :row-key="rowKeyFn"
          :row-override="pickRowOverride"
          :on-load-more="streams.extendBack"
          :has-next-page="streams.hasMore.value"
          :is-fetching-next-page="streams.isFetchingMore.value"
          :pinnable-header="false"
        />
      </div>
    </div>

    <!-- Desktop: two grey buttons matching PaymentCard / BusinessServiceCard. -->
    <div v-if="!hideEditBtn && (canEdit || canDelete)" class="client-card__footer-actions">
      <button
        v-if="canDelete"
        type="button"
        class="client-card__footer-btn"
        :disabled="deleteMutation.isPending.value"
        @click="handleDelete"
      >
        {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
      </button>
      <button v-if="canEdit" type="button" class="client-card__footer-btn" @click="openEdit">
        {{ t('common.edit') }}
      </button>
    </div>
    <!-- Mobile (ClientCardMobileScreen): single full-width red Delete row. -->
    <div v-else-if="hideEditBtn && canDelete" class="client-card__danger-group">
      <button
        type="button"
        class="client-card__danger-row client-card__danger-row--mobile"
        :disabled="deleteMutation.isPending.value"
        @click="handleDelete"
      >
        {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
      </button>
    </div>

    <DeleteConfirmSheet
      :open="showDeleteConfirm"
      :centered="!hideEditBtn"
      :title="t('clientCard.deleteTitle')"
      :description="deleteDescription"
      :pending="deleteMutation.isPending.value"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </section>
</template>

<style scoped>
.client-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: 100%;
  max-width: 22rem;
  margin: 0 auto;
  background: var(--color-tufte-bg);
  font-size: var(--text-tufte-sm);
  overflow: hidden;
}

.client-card__header {
  padding: 0.7rem 1rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  flex-shrink: 0;
}

.client-card__scroll {
  /* One scroll area for everything under the name row, ledger included. */
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.client-card__header-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.client-card__header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.client-card__edit-btn {
  appearance: none;
  background: none;
  border: none;
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.client-card__edit-btn:hover {
  color: var(--color-tufte-text);
}

.client-card__delete-btn-top {
  appearance: none;
  background: none;
  border: none;
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.client-card__delete-btn-top:hover {
  color: var(--color-tufte-danger);
}

.client-card__name {
  font-size: var(--text-tufte-base);
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: 0.01em;
}

.client-card__meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 0.1rem;
}

.client-card__gender {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.client-card__money-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 0.6rem 1rem 0;
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
}

.client-card__money-cell {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  padding: 0.35rem 0.7rem 0.4rem;
  min-width: 0;
}

.client-card__money-cell + .client-card__money-cell {
  border-left: 1px solid var(--color-tufte-border-light);
}

.client-card__money-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.client-card__money-value {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-lg);
  font-weight: 600;
  line-height: 1.25;
}

.client-card__money-value--neg {
  color: var(--color-money-negative);
}

.client-card__money-value--pos {
  color: var(--color-money-positive);
}

.client-card__money-value--muted {
  color: var(--color-tufte-muted);
}

.client-card__subs {
  margin: 0.6rem 1rem 0;
}

.client-card__subs-list {
  border: 1px solid color-mix(in srgb, var(--color-subscription-ink) 18%, var(--color-tufte-border));
  border-radius: 6px;
  overflow: hidden;
}

.client-card__subs-item {
  background: var(--color-subscription-band);
  padding: 0.4rem 0.7rem 0.45rem;
}

.client-card__subs-item + .client-card__subs-item {
  border-top: 1px solid
    color-mix(in srgb, var(--color-subscription-ink) 18%, var(--color-tufte-border));
}

.client-card__subs-name {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
  font-size: var(--text-tufte-lg);
  line-height: 1.25;
  color: var(--color-subscription-ink);
}

.client-card__subs-dates {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-xs);
  line-height: 1.2;
  color: color-mix(in srgb, var(--color-subscription-ink) 80%, var(--color-tufte-muted));
}

.client-card__subs-used {
  flex-shrink: 0;
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  color: var(--color-subscription-ink);
}

.client-card__subs-used--spent {
  /* Quota used up: amber, as in the sketch. */
  color: #8a5a1a;
}

.client-card__subs-empty {
  padding: 0.1rem 0;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  font-style: italic;
  color: var(--color-tufte-muted);
}

.client-card__stats {
  padding: 0.55rem 1rem 0.6rem;
  border-bottom: 1px solid var(--color-tufte-border);
  flex-shrink: 0;
}

.client-card__stat-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  line-height: 1.5;
  font-size: var(--text-tufte-sm);
}

.client-card__stat-label {
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte-sans);
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1.2;
}

.client-card__stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.45rem;
}

.client-card__stat-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.client-card__stat-value {
  font-size: var(--text-tufte-sm);
}

.client-card__stat-value--success {
  color: var(--color-money-positive);
}

.client-card__stat-value--stale {
  color: var(--color-visit-stale);
}

.client-card__contact {
  padding: 0.45rem 1rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.8rem;
}

.client-card__contact-item {
  font-size: var(--text-tufte-xs);
  overflow-wrap: anywhere;
  font-family: var(--font-tufte-mono);
  color: var(--color-tufte-text);
}

.client-card__contact-item--muted {
  color: var(--color-tufte-muted);
}

.client-card__spark {
  padding: 0.5rem 1rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  flex-shrink: 0;
}

.client-card__spark-label {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-bottom: 0.2rem;
}

.client-card__visits {
  /* Plain block inside the scroll area: grows with its rows. */
  display: flex;
  flex-direction: column;
}

.client-card__visits-header {
  padding: 0.45rem 1rem 0.3rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  flex-shrink: 0;
}

.client-card__visits-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  flex: 1 1 0;
}

.client-card__loading-gif {
  display: block;
  max-width: 100%;
  height: auto;
}

/*
 * The default `DynamicTable` font-size of `--text-tufte-sm` is too wide for
 * a 22rem panel. Shrinking the table inside the card to `--text-tufte-xs`
 * brings the four columns inside the available width without horizontal
 * scrolling, and matches the mock's `0.78rem` body density.
 */
.client-card__visits-table :deep(.dynamic-table) {
  font-size: var(--text-tufte-xs);
  width: 100%;
}

.client-card__visits-table :deep(.dynamic-table__th),
.client-card__visits-table :deep(td) {
  padding: 0.3rem 0.4rem;
}

.client-card__visits-table :deep(td:first-child),
.client-card__visits-table :deep(.dynamic-table__th:first-child) {
  /* Visit-number column. Tight gutter on both sides (`0.35rem`) so the
   * cell hugs the digit itself; the user's request was that the first
   * column be "approximately the same width as number". The default
   * `0.4rem` right padding from the body rule above is also overridden
   * here so the spacing stays symmetric and the column hands off to
   * the date cell with a single-character gap rather than the
   * regular 0.75rem indent that the other columns use. */
  padding-left: 0.35rem;
  padding-right: 0.25rem;
}

.client-card__visits-table :deep(td:last-child),
.client-card__visits-table :deep(.dynamic-table__th:last-child) {
  padding-right: 0.75rem;
}

/*
 * Date column (column 2 — `#` is column 1). The cell renders a single
 * pre-formatted string like "27 Nov 2025"; `white-space: nowrap` here
 * keeps that string on one line even when the surrounding panel is
 * narrow, so the user never sees "27" / "Nov" / "2025" stacked. The
 * column's fixed `width` reserves enough room for the longest medium
 * date format the supported locales produce; `TextCell` itself does
 * not opinionate on whitespace, so this rule is the canonical place
 * for the policy and lives next to the column that needs it.
 */
.client-card__visits-table :deep(td:nth-child(2)),
.client-card__visits-table :deep(.dynamic-table__th:nth-child(2)) {
  white-space: nowrap;
}

/*
 * Type column (column 3). `vertical-align: top` lines the wrapped
 * service-type name up with the date in column 2 instead of centering it
 * vertically across an extra-tall row, which matches how spreadsheet-style
 * ledgers traditionally read. The Type cell's own scoped CSS already sets
 * this, but the `:deep` rule pulls the same alignment up to neighbouring
 * cells (date / Δ / Bal.) so all four sit on the first wrapped line.
 */
.client-card__visits-table :deep(td) {
  vertical-align: top;
}

/*
 * Δ ($) and Bal. ($) columns. The mono digits read as visually heavier
 * than the proportional text in the date / type cells next to them, so
 * we drop their font-size below the table's `--text-tufte-xs` baseline.
 * `calc(... - 0.05rem)` is half the 0.1rem step between
 * `--text-tufte-sm` and `--text-tufte-xs`: the previous full-step
 * override (`-0.1rem`) felt too small in practice, so we walk it back
 * by half. The selector pins the rule to the last two `td` columns
 * (signed delta + running balance), leaving the same components free
 * to render at default density when reused elsewhere.
 */
.client-card__visits-table :deep(td:nth-last-child(-n + 2)) {
  font-size: calc(var(--text-tufte-xs) - 0.05rem);
}

/*
 * A payment that bought a subscription is followed by its full-width
 * "for subscription «…»" note row; drop the separator under the payment so the
 * note reads as part of it (the note row keeps its own bottom border).
 */
.client-card__visits-table
  :deep(.dynamic-table__tr:has(+ .dynamic-table__tr .event-row-cell--payment-note)) {
  border-bottom: none;
}

/* ── Danger group (iPhone only) ──────────────────────────────────────────── */
.client-card__danger-group {
  flex-shrink: 0;
}

.client-card__danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1rem;
  text-align: center;
  font-family: var(--font-tufte-sans);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
/* Mobile screen (ClientCardMobileScreen): matches every other entity's
 * mobile detail screen, which use `font: inherit` (serif, per the app-wide
 * iPhone button-font convention) at a larger 0.95rem size. */
.client-card__danger-row--mobile {
  font: inherit;
  font-size: 0.95rem;
}
.client-card__danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.client-card__danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ── Desktop footer: matches PaymentCard / BusinessServiceCard ──────────── */
.client-card__footer-actions {
  flex-shrink: 0;
  border-top: 1px solid var(--color-tufte-border);
  display: flex;
}

.client-card__footer-btn {
  flex: 1;
  display: block;
  background: none;
  border: none;
  padding: 0.65rem 1rem;
  text-align: center;
  font-family: var(--font-tufte-sans);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  cursor: pointer;
}
.client-card__footer-btn:not(:last-child) {
  border-right: 1px solid var(--color-tufte-border);
}
.client-card__footer-btn:hover {
  background: color-mix(in srgb, var(--color-tufte-text) 6%, transparent);
  color: var(--color-tufte-text);
}
.client-card__footer-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
